import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { DragHandle } from '@tiptap/extension-drag-handle-react';
import StarterKit from '@tiptap/starter-kit';
import { Placeholder } from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import {
    LuBold, LuItalic, LuCode, LuLink, LuHeading2, LuQuote,
    LuType, LuHeading1, LuHeading3, LuList, LuListOrdered,
    LuSquareCheck, LuMinus, LuGripVertical, LuImage,
} from 'react-icons/lu';
import { AnotacaoService } from '../services/AnotacaoService';
import './NoteEditor.css';

interface NoteEditorProps {
    conteudoInicial: string;
    /** Nulo enquanto a anotação ainda não foi salva pela primeira vez. */
    anotacaoId: number | null;
    onChange: (html: string) => void;
    /** Enter no título desce para o corpo; isto expõe o editor para isso. */
    onReady?: (editor: Editor) => void;
}

interface ItemDeBloco {
    id: string;
    rotulo: string;
    atalho?: string;
    icone: React.ReactNode;
    aplicar: (editor: Editor) => void;
}

/* A mesma lista que aparece no menu "/" e nos atalhos de markdown. */
const BLOCOS: ItemDeBloco[] = [
    { id: 'paragrafo', rotulo: 'Texto', icone: <LuType size={15} />, aplicar: e => e.chain().focus().setParagraph().run() },
    { id: 'h1', rotulo: 'Título 1', atalho: '# ', icone: <LuHeading1 size={15} />, aplicar: e => e.chain().focus().setNode('heading', { level: 1 }).run() },
    { id: 'h2', rotulo: 'Título 2', atalho: '## ', icone: <LuHeading2 size={15} />, aplicar: e => e.chain().focus().setNode('heading', { level: 2 }).run() },
    { id: 'h3', rotulo: 'Título 3', atalho: '### ', icone: <LuHeading3 size={15} />, aplicar: e => e.chain().focus().setNode('heading', { level: 3 }).run() },
    { id: 'ul', rotulo: 'Lista com marcador', atalho: '- ', icone: <LuList size={15} />, aplicar: e => e.chain().focus().toggleBulletList().run() },
    { id: 'ol', rotulo: 'Lista numerada', atalho: '1. ', icone: <LuListOrdered size={15} />, aplicar: e => e.chain().focus().toggleOrderedList().run() },
    { id: 'task', rotulo: 'Lista de tarefas', atalho: '[] ', icone: <LuSquareCheck size={15} />, aplicar: e => e.chain().focus().toggleTaskList().run() },
    { id: 'quote', rotulo: 'Citação', atalho: '> ', icone: <LuQuote size={15} />, aplicar: e => e.chain().focus().toggleBlockquote().run() },
    { id: 'code', rotulo: 'Bloco de código', icone: <LuCode size={15} />, aplicar: e => e.chain().focus().toggleCodeBlock().run() },
    { id: 'hr', rotulo: 'Divisória', atalho: '---', icone: <LuMinus size={15} />, aplicar: e => e.chain().focus().setHorizontalRule().run() },
];

/**
 * Sobe a imagem e insere no documento. Módulo, não closure, porque a barra de
 * edição (fora do editor) e o menu "/" (dentro) usam a mesma coisa.
 */
export const enviarImagemParaAnotacao = async (
    editorAtual: Editor, arquivo: File, anotacaoId: number | null,
) => {
    try {
        const url = await AnotacaoService.uploadImagem(arquivo, anotacaoId);
        editorAtual.chain().focus().setImage({ src: url }).run();
    } catch {
        /* Sem toast: o editor vive dentro da página, que já mostra o estado de
           salvamento. Falhar em silêncio seria pior, então marca no documento
           onde a imagem não entrou. */
        editorAtual.chain().focus().insertContent('<p><em>(a imagem não pôde ser enviada)</em></p>').run();
    }
};

/** Abre o seletor de arquivo e manda para o `enviarImagemParaAnotacao`. */
export const escolherImagemDaAnotacao = (editorAtual: Editor, anotacaoId: number | null) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp,image/gif';
    input.onchange = () => {
        const arquivo = input.files?.[0];
        if (arquivo) void enviarImagemParaAnotacao(editorAtual, arquivo, anotacaoId);
    };
    input.click();
};

const NoteEditor = ({ conteudoInicial, anotacaoId, onChange, onReady }: NoteEditorProps) => {
    const areaRef = useRef<HTMLDivElement>(null);

    /* Menu "/" — posição em coordenadas da área do editor, não da janela, para
       acompanhar a rolagem sem recalcular a cada frame. */
    const [menuAberto, setMenuAberto] = useState(false);
    const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
    const [filtro, setFiltro] = useState('');
    const [indiceAtivo, setIndiceAtivo] = useState(0);
    /* Guarda a posição da "/" para apagá-la ao escolher o bloco. */
    const inicioComandoRef = useRef<number | null>(null);

    const idAnotacaoRef = useRef(anotacaoId);
    useEffect(() => { idAnotacaoRef.current = anotacaoId; }, [anotacaoId]);

    /* Preenchido depois que o editor existe; a imagem é um item do menu como
       qualquer outro, então precisa entrar na MESMA lista para ser filtrada. */
    const inserirImagemRef = useRef<() => void>(() => { });

    const visiveis = useMemo(() => {
        const todos: ItemDeBloco[] = [
            ...BLOCOS,
            { id: 'imagem', rotulo: 'Imagem', icone: <LuImage size={15} />, aplicar: () => inserirImagemRef.current() },
        ];
        const termo = filtro.toLowerCase();
        return todos.filter(b => b.rotulo.toLowerCase().includes(termo));
    }, [filtro]);

    const fecharMenu = useCallback(() => {
        setMenuAberto(false);
        setFiltro('');
        setIndiceAtivo(0);
        inicioComandoRef.current = null;
    }, []);

    const enviarImagem = useCallback(async (editorAtual: Editor, arquivo: File) => {
        await enviarImagemParaAnotacao(editorAtual, arquivo, idAnotacaoRef.current);
    }, []);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                link: { openOnClick: false },
                /* O TrailingNode do StarterKit já garante um parágrafo no fim,
                   que é o que permite clicar abaixo do último bloco e escrever. */
            }),
            Placeholder.configure({
                placeholder: ({ node }) =>
                    node.type.name === 'paragraph'
                        ? 'Escreva, ou digite "/" para os blocos'
                        : '',
            }),
            Image.configure({ inline: false }),
            TaskList,
            TaskItem.configure({ nested: true }),
        ],
        content: conteudoInicial || '',
        onUpdate: ({ editor: e }) => onChange(e.getHTML()),
        editorProps: {
            attributes: { class: 'ne-doc' },
            handlePaste: (_view, event) => {
                const arquivo = Array.from(event.clipboardData?.items ?? [])
                    .find(i => i.type.startsWith('image/'))?.getAsFile();
                if (!arquivo || !editor) return false;
                event.preventDefault();
                void enviarImagem(editor, arquivo);
                return true;
            },
            handleDrop: (_view, event) => {
                const arquivo = Array.from(event.dataTransfer?.files ?? [])
                    .find(f => f.type.startsWith('image/'));
                if (!arquivo || !editor) return false;
                event.preventDefault();
                void enviarImagem(editor, arquivo);
                return true;
            },
        },
    });

    useEffect(() => {
        if (editor && onReady) onReady(editor);
    }, [editor, onReady]);

    const aplicarBloco = useCallback((item: ItemDeBloco) => {
        if (!editor) return;
        const inicio = inicioComandoRef.current;
        if (inicio !== null) {
            /* Apaga a "/" e o que foi digitado depois dela. */
            editor.chain().focus().deleteRange({ from: inicio, to: inicio + 1 + filtro.length }).run();
        }
        item.aplicar(editor);
        fecharMenu();
    }, [editor, filtro.length, fecharMenu]);

    /* Abre, filtra e fecha o menu "/". */
    useEffect(() => {
        if (!editor) return;

        const aoTeclar = (event: KeyboardEvent) => {
            if (!menuAberto) {
                if (event.key === '/') {
                    const { from, empty } = editor.state.selection;
                    if (!empty) return;

                    /* Só abre no começo do bloco ou depois de espaço. Sem isto
                       qualquer "/" dispara o menu: medido em 12/09, escrever
                       "e/ou" ou colar "https://" abria a lista de blocos no meio
                       da frase. É a mesma regra do Notion. */
                    const anterior = editor.state.doc.textBetween(Math.max(0, from - 1), from);
                    if (anterior !== '' && !/\s/.test(anterior)) return;

                    const caixa = areaRef.current?.getBoundingClientRect();
                    const ponto = editor.view.coordsAtPos(from);
                    if (!caixa) return;
                    inicioComandoRef.current = from;
                    setMenuPos({ x: ponto.left - caixa.left, y: ponto.bottom - caixa.top + 6 });
                    setMenuAberto(true);
                    setFiltro('');
                    setIndiceAtivo(0);
                }
                return;
            }

            /* Na captura, preventDefault sozinho não basta: sem parar a
               propagação o ProseMirror ainda receberia a tecla e quebraria a
               linha por baixo do menu. */
            const meu = (e: KeyboardEvent) => { e.preventDefault(); e.stopPropagation(); };

            if (event.key === 'Escape') { meu(event); fecharMenu(); return; }
            if (event.key === 'ArrowDown') {
                meu(event);
                setIndiceAtivo(i => (i + 1) % Math.max(visiveis.length, 1));
                return;
            }
            if (event.key === 'ArrowUp') {
                meu(event);
                setIndiceAtivo(i => (i - 1 + visiveis.length) % Math.max(visiveis.length, 1));
                return;
            }
            if (event.key === 'Enter' || event.key === 'Tab') {
                meu(event);
                const escolhido = visiveis[indiceAtivo];
                if (escolhido) aplicarBloco(escolhido);
                else fecharMenu();
                return;
            }
            if (event.key === 'Backspace') {
                if (filtro.length === 0) { fecharMenu(); return; }
                setFiltro(f => f.slice(0, -1));
                return;
            }
            if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
                setFiltro(f => f + event.key);
                setIndiceAtivo(0);
            }
        };

        /* CAPTURA, não borbulha. Medido em 11/09: na fase de bolha o Enter e as
           setas NUNCA chegavam aqui — o ProseMirror trata e interrompe antes,
           então o menu abria, filtrava, e o Enter quebrava a linha em vez de
           escolher o bloco. Na captura o menu decide primeiro e só deixa passar
           o que não é dele. */
        const dom = editor.view.dom;
        dom.addEventListener('keydown', aoTeclar, true);
        return () => dom.removeEventListener('keydown', aoTeclar, true);
    }, [editor, menuAberto, visiveis, indiceAtivo, filtro, fecharMenu, aplicarBloco]);

    /* O menu "/" fica aberto por cima do texto; sair do editor tem que fechá-lo,
       senão ele reaparece com o filtro da vez anterior. */
    useEffect(() => {
        if (!editor || !menuAberto) return;
        const dom = editor.view.dom;
        dom.addEventListener('blur', fecharMenu);
        return () => dom.removeEventListener('blur', fecharMenu);
    }, [editor, menuAberto, fecharMenu]);

    useEffect(() => {
        inserirImagemRef.current = () => {
            if (editor) escolherImagemDaAnotacao(editor, idAnotacaoRef.current);
        };
    }, [editor]);

    if (!editor) return <div className="ne-carregando" />;

    return (
        <div className="ne-area" ref={areaRef}>
            <DragHandle editor={editor}>
                <span className="ne-grip" title="Arrastar bloco">
                    <LuGripVertical size={15} />
                </span>
            </DragHandle>

            <BubbleMenu editor={editor} className="ne-bubble">
                <button type="button" className={editor.isActive('bold') ? 'is-on' : ''}
                    onClick={() => editor.chain().focus().toggleBold().run()} title="Negrito">
                    <LuBold size={14} />
                </button>
                <button type="button" className={editor.isActive('italic') ? 'is-on' : ''}
                    onClick={() => editor.chain().focus().toggleItalic().run()} title="Itálico">
                    <LuItalic size={14} />
                </button>
                <button type="button" className={editor.isActive('code') ? 'is-on' : ''}
                    onClick={() => editor.chain().focus().toggleCode().run()} title="Código">
                    <LuCode size={14} />
                </button>
                <button type="button" className={editor.isActive('link') ? 'is-on' : ''}
                    onClick={() => {
                        const atual = editor.getAttributes('link').href as string | undefined;
                        const url = window.prompt('Endereço do link', atual ?? 'https://');
                        if (url === null) return;
                        if (url.trim() === '') {
                            editor.chain().focus().unsetLink().run();
                            return;
                        }
                        editor.chain().focus().setLink({ href: url.trim() }).run();
                    }} title="Link">
                    <LuLink size={14} />
                </button>
                <span className="ne-bubble-sep" />
                <button type="button" className={editor.isActive('heading', { level: 2 }) ? 'is-on' : ''}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Título 2">
                    <LuHeading2 size={14} />
                </button>
                <button type="button" className={editor.isActive('blockquote') ? 'is-on' : ''}
                    onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Citação">
                    <LuQuote size={14} />
                </button>
            </BubbleMenu>

            <EditorContent editor={editor} />

            {menuAberto && (
                <div className="ne-slash" style={{ left: menuPos.x, top: menuPos.y }} role="listbox">
                    <div className="ne-slash-head">Blocos</div>
                    {visiveis.length === 0 && <div className="ne-slash-vazio">Nenhum bloco com esse nome</div>}
                    {visiveis.map((item, i) => (
                        <button
                            key={item.id}
                            type="button"
                            role="option"
                            aria-selected={i === indiceAtivo}
                            className={`ne-slash-item ${i === indiceAtivo ? 'is-on' : ''}`}
                            onMouseDown={e => { e.preventDefault(); aplicarBloco(item); }}
                        >
                            {item.icone}
                            <span>{item.rotulo}</span>
                            {item.atalho && <code>{item.atalho}</code>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default NoteEditor;
